import React, { forwardRef, useEffect, useId, useRef, useState } from 'react';
import { currencySymbol } from '../../../utils/currency';
import { DEFAULT_DIAL, phonePlaceholder, splitE164 } from './phone-codes';
import { PhoneCountrySelect } from './PhoneCountrySelect';
import type { InputProps, InputSize } from './types';

// ─── Size class map (exported for snapshot testing) ──────────────────────────

export const SIZE_CLASSES: Record<InputSize, { height: string; padding: string; text: string }> = {
  sm: { height: 'h-8 min-h-[44px] sm:min-h-0', padding: 'px-3 py-1', text: 'text-sm' },
  md: { height: 'h-11', padding: 'px-3 py-2', text: 'text-sm' },
  lg: { height: 'h-12', padding: 'px-4 py-3', text: 'text-base' },
};

// ─── Inline spinner (mirrors ButtonSpinner) ───────────────────────────────────

function InputSpinner() {
  return (
    <span
      className="inline-block animate-spin text-neutral-400 dark:text-neutral-500 pr-3"
      role="status"
      aria-label="Loading"
    >
      <svg
        className="h-4 w-4"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
    </span>
  );
}

// ─── Search icon ──────────────────────────────────────────────────────────────

function SearchIcon() {
  return (
    <svg
      className="h-4 w-4 text-neutral-400 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
      <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// ─── Barcode icon ─────────────────────────────────────────────────────────────

function BarcodeIcon() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="2" y="4" width="2" height="16" fill="currentColor" />
      <rect x="6" y="4" width="1" height="16" fill="currentColor" />
      <rect x="9" y="4" width="2" height="16" fill="currentColor" />
      <rect x="13" y="4" width="1" height="16" fill="currentColor" />
      <rect x="16" y="4" width="2" height="16" fill="currentColor" />
      <rect x="20" y="4" width="2" height="16" fill="currentColor" />
    </svg>
  );
}

// ─── Input ────────────────────────────────────────────────────────────────────

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    type = 'text',
    variant = 'default',
    currency = 'INR',
    defaultDial = DEFAULT_DIAL,
    label,
    placeholder,
    value,
    onChange,
    onKeyDown,
    error,
    helperText,
    disabled = false,
    required = false,
    prefix,
    suffix,
    size = 'md',
    min,
    max,
    maxLength,
    step: stepProp,
    onScan,
    loading = false,
    id: idProp,
    name,
    list,
    inputMode,
    className,
    inputClassName,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedByProp,
  },
  ref,
) {
  // The quantity stepper moves by 1 unless told otherwise.
  const step = stepProp ?? 1;
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const errorId = `${id}-error`;
  const helperId = `${id}-helper`;

  const s = SIZE_CLASSES[size];
  const hasError = Boolean(error);

  // ─── Phone variant: country dial-code dropdown + E.164 value contract ────
  // The controlled `value` is the full E.164 string (e.g. "+919876543210").
  // We keep the selected dial code in state and show only the national digits
  // in the text box; onChange emits the recomposed E.164 string.
  const parsedPhone =
    variant === 'phone' ? splitE164(typeof value === 'string' ? value : '', defaultDial) : null;
  const [phoneDial, setPhoneDial] = useState<string>(parsedPhone?.dial ?? defaultDial);
  const phoneNational = parsedPhone?.national ?? '';
  // Set once the user picks a country themselves, so a later `defaultDial`
  // change never overrides a deliberate choice.
  const dialPickedByUser = useRef(false);

  // Sync the dropdown when a value with an explicit dial code arrives (e.g. an
  // edit form hydrating from the API after mount). The value wins over both the
  // default and the user's pick — it is the record's own country code.
  useEffect(() => {
    if (variant !== 'phone') return;
    if (typeof value === 'string' && value.trim().startsWith('+')) {
      const { dial } = splitE164(value, defaultDial);
      setPhoneDial((prev) => (dial !== prev ? dial : prev));
    }
  }, [value, variant, defaultDial]);

  // Adopt a `defaultDial` that arrives after mount. The tenant currency is not
  // known on the first render — `useTenantCurrency()` reports INR until the auth
  // profile hydrates — and the `useState` initialiser above only ever runs once,
  // so without this a form mounted during that window is stuck on +91 forever.
  // Only applies while the field carries no country code of its own and the user
  // has not picked one: those two cases are handled above and by the picker.
  useEffect(() => {
    if (variant !== 'phone') return;
    if (dialPickedByUser.current) return;
    if (typeof value === 'string' && value.trim().startsWith('+')) return;
    setPhoneDial((prev) => (defaultDial !== prev ? defaultDial : prev));
  }, [defaultDial, value, variant]);

  const emitPhone = (dial: string, national: string) => {
    const digits = national.replace(/\D/g, '').slice(0, 14);
    onChange?.(digits ? `${dial}${digits}` : '');
  };

  // ─── Determine effective input type per variant ─────────────────────────
  const effectiveType = variant === 'phone' ? 'tel' : variant === 'quantity' ? 'number' : type;

  // ─── Determine numeric input mode ──────────────────────────────────────
  const effectiveInputMode =
    inputMode ?? (variant === 'currency' || variant === 'phone' ? 'numeric' : undefined);

  // ─── Derive left/right addons from variant (overridable by prefix/suffix) ─

  // quantity stepper helpers
  const numericValue = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  const atMin = min !== undefined && numericValue <= min;
  const atMax = max !== undefined && numericValue >= max;

  const handleDecrease = () => {
    if (disabled || atMin) return;
    const next = numericValue - step;
    onChange?.(min !== undefined ? String(Math.max(next, min)) : String(next));
  };

  const handleIncrease = () => {
    if (disabled || atMax) return;
    const next = numericValue + step;
    onChange?.(max !== undefined ? String(Math.min(next, max)) : String(next));
  };

  let variantLeft: React.ReactNode = null;
  let variantRight: React.ReactNode = null;

  if (variant === 'currency') {
    variantLeft = (
      <span className="text-neutral-500 dark:text-neutral-400 pl-3 select-none shrink-0 text-sm">
        {currencySymbol(currency)}
      </span>
    );
  } else if (variant === 'phone') {
    variantLeft = (
      <PhoneCountrySelect
        value={phoneDial}
        onChange={(dial) => {
          dialPickedByUser.current = true;
          setPhoneDial(dial);
          emitPhone(dial, phoneNational);
        }}
        disabled={disabled}
      />
    );
  } else if (variant === 'search') {
    variantLeft = (
      <span className="pl-3 flex items-center shrink-0">
        <SearchIcon />
      </span>
    );
    if (value !== '' && value !== undefined) {
      variantRight = (
        <button
          type="button"
          className="pr-3 flex items-center text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
          onClick={() => onChange?.('')}
          aria-label="Clear search"
          tabIndex={0}
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M18 6 6 18M6 6l12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      );
    }
  } else if (variant === 'quantity') {
    variantLeft = (
      <button
        type="button"
        className={[
          'h-full px-3 border-r border-neutral-300 dark:border-neutral-600',
          'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700',
          'transition-colors duration-150 shrink-0 flex items-center',
          atMin || disabled ? 'opacity-40 cursor-not-allowed' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={handleDecrease}
        disabled={atMin || disabled}
        aria-label="Decrease quantity"
        tabIndex={0}
      >
        −
      </button>
    );
    variantRight = (
      <button
        type="button"
        className={[
          'h-full px-3 border-l border-neutral-300 dark:border-neutral-600',
          'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700',
          'transition-colors duration-150 shrink-0 flex items-center',
          atMax || disabled ? 'opacity-40 cursor-not-allowed' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={handleIncrease}
        disabled={atMax || disabled}
        aria-label="Increase quantity"
        tabIndex={0}
      >
        +
      </button>
    );
  } else if (variant === 'barcode') {
    variantRight = (
      <button
        type="button"
        className="pr-3 flex items-center text-neutral-500 hover:text-brand-600 dark:text-neutral-400 dark:hover:text-brand-400 transition-colors duration-150 shrink-0"
        onClick={onScan}
        aria-label="Scan barcode"
        tabIndex={0}
      >
        <BarcodeIcon />
      </button>
    );
  }

  // explicit prefix/suffix override variant addons
  const leftAddon = prefix !== undefined ? prefix : variantLeft;
  const rightAddon = loading ? (
    <InputSpinner />
  ) : suffix !== undefined ? (
    suffix
  ) : (
    variantRight
  );

  // Remove padding from input side where addon is present
  const hasLeft = leftAddon !== null;
  const hasRight = rightAddon !== null;

  // ─── Class assembly ─────────────────────────────────────────────────────

  const rowClasses = [
    'flex items-center w-full border rounded bg-white overflow-hidden transition-colors duration-150',
    'dark:bg-neutral-800 dark:border-neutral-600 dark:text-neutral-100',
    s.height,
    hasError
      ? 'border-danger-500 focus-within:ring-2 focus-within:ring-danger-500 focus-within:ring-offset-0'
      : 'border-neutral-300 hover:border-neutral-400 focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-brand-500 focus-within:ring-offset-0',
    disabled ? 'bg-neutral-100 dark:bg-neutral-700 cursor-not-allowed opacity-70 pointer-events-none' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const inputClasses = [
    'flex-1 h-full bg-transparent outline-none border-none min-w-0',
    'placeholder:text-neutral-400 dark:placeholder:text-neutral-500',
    'text-neutral-900 dark:text-neutral-100',
    s.text,
    hasLeft && hasRight
      ? 'px-2'
      : hasLeft
        ? `pr-3 ${s.padding.split(' ').find(c => c.startsWith('py-')) ?? 'py-2'}`
        : hasRight
          ? `pl-3 ${s.padding.split(' ').find(c => c.startsWith('py-')) ?? 'py-2'}`
          : s.padding,
    disabled ? 'cursor-not-allowed' : '',
    variant === 'currency' ? 'text-right tabular-nums' : '',
    variant === 'phone' ? 'pl-3' : '',
    variant === 'quantity'
      ? 'text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
      : '',
    inputClassName ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  // ─── Change handlers ────────────────────────────────────────────────────

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (variant === 'phone') {
      emitPhone(phoneDial, e.target.value);
    } else {
      onChange?.(e.target.value);
    }
  };

  // ─── aria-describedby ───────────────────────────────────────────────────
  const describedBy = ariaDescribedByProp ?? (hasError ? errorId : helperText ? helperId : undefined);

  return (
    <div className={['flex flex-col gap-1', className ?? ''].filter(Boolean).join(' ')}>
      {label && (
        <label
          htmlFor={id}
          className="text-sm font-medium text-neutral-700 dark:text-neutral-300"
        >
          {label}
          {required && (
            <span className="text-danger-500 ml-1" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      <div className={rowClasses}>
        {leftAddon}
        <input
          ref={ref}
          id={id}
          name={name}
          type={effectiveType}
          inputMode={effectiveInputMode}
          placeholder={placeholder ?? (variant === 'phone' ? phonePlaceholder(phoneDial) : undefined)}
          value={variant === 'phone' ? phoneNational : (value ?? '')}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          disabled={disabled}
          required={required}
          aria-required={required || undefined}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          aria-label={ariaLabel}
          min={min}
          max={max}
          maxLength={maxLength}
          // Any other number input gets the caller's step, so a money field can allow
          // the currency's decimals: without one the browser steps by 1 and refuses
          // to submit "1.234" in a native form.
          step={variant === 'quantity' ? step : stepProp}
          list={list}
          className={inputClasses}
          autoComplete={variant === 'phone' ? 'tel' : undefined}
        />
        {rightAddon}
      </div>

      {hasError && (
        <span id={errorId} role="alert" className="text-xs text-danger-600 dark:text-danger-400">
          {error}
        </span>
      )}
      {!hasError && helperText && (
        <span id={helperId} className="text-xs text-neutral-500 dark:text-neutral-400">
          {helperText}
        </span>
      )}
    </div>
  );
});
